import type { ReactElement } from 'react';

import { Skeleton } from '@/components/ui/skeleton';

/**
 * Per-route skeleton for `/settings` and `/settings/profile`. Mirrors
 * the real settings layout — page header → sidebar nav + about footer
 * (desktop) → section content with the active hero on top. On mobile,
 * the hub of section cards is the dominant skeleton.
 *
 * No internal `DelayedSkeleton` — the parent `RouteSkeleton` wraps the
 * returned element in one already.
 */
const NAV_ITEMS = [
  'sk-nav-1',
  'sk-nav-2',
  'sk-nav-3',
  'sk-nav-4',
  'sk-nav-5',
] as const;

const HUB_CARDS = [
  'sk-hub-1',
  'sk-hub-2',
  'sk-hub-3',
  'sk-hub-4',
  'sk-hub-5',
] as const;

const ABOUT_ROWS = ['sk-about-1', 'sk-about-2', 'sk-about-3'] as const;

function SidebarNavSkeleton(): ReactElement {
  return (
    <div className="space-y-3">
      <div className="rounded-2xl border border-border/70 bg-card/80 p-2 shadow-sm">
        <div className="space-y-1">
          {NAV_ITEMS.map((key) => (
            <div key={key} className="flex items-center gap-3 px-3 py-2.5">
              <Skeleton className="h-9 w-9 flex-shrink-0 rounded-lg" />
              <div className="min-w-0 flex-1 space-y-1.5">
                <Skeleton className="h-3.5 w-24" />
                <Skeleton className="h-2.5 w-32" />
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="rounded-2xl border border-border/50 bg-card/50 p-3 shadow-sm">
        <div className="mb-2 flex items-center gap-2">
          <Skeleton className="h-6 w-6 rounded-md" />
          <Skeleton className="h-2.5 w-12" />
        </div>
        <div className="space-y-2">
          {ABOUT_ROWS.map((key) => (
            <div key={key} className="flex items-center justify-between">
              <Skeleton className="h-2.5 w-16" />
              <Skeleton className="h-2.5 w-12" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ProfileHeroSectionSkeleton(): ReactElement {
  return (
    <section className="overflow-hidden rounded-2xl border border-border/60 bg-card/80 shadow-sm">
      <Skeleton className="h-44 w-full rounded-none" />
      <div className="px-4 pb-5 pt-3 sm:px-6">
        <Skeleton className="-mt-14 mb-3 h-24 w-24 rounded-full sm:-mt-16 sm:h-28 sm:w-28" />
        <Skeleton className="h-6 w-1/3" />
        <Skeleton className="mt-2 h-3 w-1/2" />
        <div className="mt-4 space-y-2">
          <Skeleton className="h-3 w-3/4" />
          <Skeleton className="h-3 w-2/3" />
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <Skeleton className="h-5 w-28 rounded-full" />
          <Skeleton className="h-5 w-32 rounded-full" />
        </div>
      </div>
    </section>
  );
}

function AccountRowsSkeleton(): ReactElement {
  return (
    <div className="space-y-3">
      <Skeleton className="h-20 w-full rounded-2xl" />
      {ABOUT_ROWS.map((key) => (
        <Skeleton key={key} className="h-16 w-full rounded-2xl" />
      ))}
    </div>
  );
}

function MobileHubSkeleton(): ReactElement {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:hidden">
      {HUB_CARDS.map((key) => (
        <div
          key={key}
          className="flex items-start gap-3 rounded-2xl border border-border/70 bg-card/80 p-4 shadow-sm"
        >
          <Skeleton className="h-11 w-11 flex-shrink-0 rounded-xl" />
          <div className="min-w-0 flex-1 space-y-1.5">
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-3 w-3/4" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function SettingsSkeleton(): ReactElement {
  // Plain `<div>` (not `<main>`) so it composes inside the route-level
  // layout `<main>` without nesting a second landmark.
  return (
    <div className="mx-auto w-full max-w-6xl px-3 pb-16 pt-6 sm:px-6 sm:pt-10">
      {/* Page header */}
      <div className="mb-6 space-y-2 md:mb-8">
        <Skeleton className="h-8 w-32 md:h-10 md:w-40" />
        <Skeleton className="h-4 w-72 max-w-full md:h-5" />
      </div>

      {/* Mobile hub */}
      <MobileHubSkeleton />

      {/* Desktop split */}
      <div className="hidden lg:block">
        <div className="grid grid-cols-[280px_minmax(0,1fr)] gap-8 xl:grid-cols-[300px_minmax(0,1fr)] xl:gap-10">
          <SidebarNavSkeleton />
          <div className="min-w-0 space-y-6">
            <ProfileHeroSectionSkeleton />
            <AccountRowsSkeleton />
          </div>
        </div>
      </div>
    </div>
  );
}

export default SettingsSkeleton;
